/**
 * Kiosk Settings Types
 * Types for the Kiosk tab configuration
 * Maps to /kiosk/v1/admin/operator?operatorId={id} endpoint
 */

/** Shown when an operator (or its resolved parent, for a sub-operator) has no OCPI party to save under */
export const NO_ASSOCIATED_PARTY_ERROR = 'This operator has no associated party — cannot save';
export const PRE_AUTH_INVALID_ERROR = 'Pre-Auth preset amounts are invalid - fix them before saving';

/**
 * Connected Status Enum - Charging connector connected status
 */
export enum ConnectedStatusEnum {
  OCPP = 'OCPP',
  BLOCKED = 'Blocked',
  RESERVED = 'Reserved'
}

/**
 * Allowed Characters Enum - License plate allowed character types
 */
export enum AllowedCharactersEnum {
  NUMERIC = 0,
  ALPHANUMERIC = 1,
  HEBREW = 2,
  UNKNOWN = 999
}

/**
 * Connected Action Type Enum - Action when charging connector is connected
 */
export enum ConnectedActionEnum {
  DISABLED = 'Disabled',
  PAYMENT = 'Payment',
  PRICING = 'Pricing',
  UNKNOWN = 'Unknown'
}

/**
 * RFID card authorisation flow support
 * enum: _UNKNOWN(null), REMOTE_START("REMOTE_START")
 */
export enum SupportRfidFlowEnum {
  REMOTE_START = 'REMOTE_START',
}

/**
 * Grace Period source field — controls which OCPI field the kiosk reads for grace period value.
 * ABSENT: neither field received; grace period UI is hidden (default when key is missing)
 * EXTERNAL: reads min_dimension_duration from CSMS tariff element (idle-level granularity)
 * MIN_DURATION: reads minimum_duration from CSMS tariff element (session-level granularity)
 */
export enum MinDimDurationSourceEnum {
  ABSENT = 'ABSENT',
  EXTERNAL = 'EXTERNAL',
  MIN_DURATION = 'MIN_DURATION',
}

/**
 * Stop Session Mode - how the stop-session action is authorized at the payment terminal
 * StopWithCard: session can only be stopped by presenting the initiating card/RFID (default)
 * StopWithoutCardConfirm: session can be stopped without card; user sees a confirmation prompt
 * StopWithoutCardInstant: session can be stopped without card; stops immediately
 */
export enum StopSessionModeEnum {
  STOP_WITH_CARD = 1,
  STOP_WITHOUT_CARD_INSTANT = 2,
  STOP_WITHOUT_CARD_CONFIRM = 3,
}

/**
 * License Plate Settings
 * Controls for license plate capture and validation
 */
export interface LicensePlateSettings {
  /** Enable license plate capture */
  enabled?: boolean;

  /** Make license plate mandatory */
  isMandatory?: boolean;

  /** Allowed characters type (enum index) */
  allowedCharacters?: number;
}

export const PHONE_PATTERN_OPTIONS = {
  NONE:   '^[0-9]{1,15}$',
  ISRAEL: '^05[0-9]{8}$',
} as const;

export interface PhoneNumberSettings {
  /** Enable phone number capture */
  enabled?: boolean;

  /** Make phone number mandatory */
  isMandatory?: boolean;

  /** Regex pattern for phone number validation */
  phonePattern?: string;
}

/**
 * Pre-Auth Selection settings (PS-389)
 * JSON: "preAuth": { "enabled": true, "values": [1, 2, 3, 4] }
 * In the store `values` is always 4 slots (null = empty box); persisted as numbers only.
 */
export interface PreAuthSettings {
  /** Show the pre-auth amount selection screen on the kiosk */
  enabled: boolean;

  /** Exactly 4 preset amounts, whole major-currency units, in display order */
  values: (number | null)[];
}

/**
 * Idle Screen Settings
 * Configuration for idle screen display when charging is waiting
 */
export interface IdleScreenSettings {
  /** Enable idle screen display */
  enabled?: boolean;

  /** Image URL to display on idle screen (5-inch display) */
  imageUrl?: string;

  /** Media URL for 4-inch display */
  media4Url?: string;

  /** Timeout in milliseconds before showing idle screen */
  timeout?: number;
}

/**
 * Connected Action Settings
 * Action to perform when charging connector is connected
 */
export interface ConnectedActionSettings {
  /** Action type (Disabled, Payment, Pricing) */
  type?: string;

  /** Duration in milliseconds for the action */
  time?: number;
}

/**
 * Kiosk Settings - Stringified JSON from API response
 * Contains all kiosk-specific configuration
 */
export interface KioskSettingsJson {
  // ──────────────────────────────────────────────────────────────────────
  // GENERAL
  // ──────────────────────────────────────────────────────────────────────

  /** CPO Password (6-10 characters) */
  cpoPassword?: string;

  /**
   * Whether connection-status-based features are supported.
   * Defaults to true when absent. When explicitly false, the following fields
   * are disabled: connectionRequired, isConnectedSorting, connectedAction.
   */
  connectedStatusSupport?: boolean;

  /** Connected status (OCPP, Blocked, Reserved) */
  connected?: string;

  /** Logo URL for the kiosk display */
  logo?: string;

  /** Connected Sorting - Enable sorting by connection status */
  isConnectedSorting?: boolean;

  /** Grace Period source field — which OCPI field the kiosk reads for grace period value */
  MinDimDurationSource?: MinDimDurationSourceEnum;

  // ──────────────────────────────────────────────────────────────────────
  // LANGUAGES
  // ──────────────────────────────────────────────────────────────────────

  /** Default language code (e.g., "en", "he") */
  language?: string;

  /** Supported language codes array (e.g., ["en", "he"]) */
  supportedLanguages?: string[];

  // ──────────────────────────────────────────────────────────────────────
  // SCREENS
  // ──────────────────────────────────────────────────────────────────────

  /** Require network connection before charging */
  connectionRequired?: boolean;

  /** Hide stop button from UI */
  hideStopButton?: boolean;

  /** Display tariff in 24-hour non-relative format */
  tariff24HoursDisplayNonRelative?: boolean;

  /** Session timeout in milliseconds */
  sessionTimeout?: number;

  /** Idle screen configuration */
  idleScreen?: IdleScreenSettings;

  /** Connected action configuration */
  connectedAction?: ConnectedActionSettings;

  // ──────────────────────────────────────────────────────────────────────
  // PAYMENT
  // ──────────────────────────────────────────────────────────────────────

  /** AC credit buffer (in kWh) - Default: 1.0 */
  acCreditBuffer?: number;

  /** DC credit buffer (in kWh) - Default: 2.0 */
  dcCreditBuffer?: number;

  /** Minimum cost per minute threshold */
  minCostPerMinuteThreshold?: number;

  /** Support incremental authorization for payments */
  isSupportedIncrementalAuth?: boolean;

  /** Maximum transaction fee percentage */
  maxTransactionFeePercentage?: number;

  /** Average kWh cost for estimation */
  averageKwhCost?: number;

  /** RFID card authorisation flow support — null = unknown/unset */
  supportRfidFlow?: SupportRfidFlowEnum | null;

  /** Stop session mode — controls how stop-session is authorized at the payment terminal */
  stopSessionMode?: StopSessionModeEnum;

  /**
   * Minimum auth approval ratio (0–1).
   * Blocks session start if approved_amount < requested_amount * ratio.
   * 0 = feature disabled. Default: 0.10
   */
  authApprovalMinRatio?: number;

  // ──────────────────────────────────────────────────────────────────────
  // LICENSE PLATE
  // ──────────────────────────────────────────────────────────────────────

  /** License plate capture settings */
  licensePlate?: LicensePlateSettings;

  // ──────────────────────────────────────────────────────────────────────
  // PHONE NUMBER
  // ──────────────────────────────────────────────────────────────────────

  /** Phone number capture settings */
  phoneNumber?: PhoneNumberSettings;

  // ──────────────────────────────────────────────────────────────────────
  // PRE-AUTH SELECTION
  // ──────────────────────────────────────────────────────────────────────

  /** Pre-auth amount selection screen + 4 preset amounts */
  preAuth?: PreAuthSettings;
}

/**
 * DbActor - Operator data from API response
 * Contains both basic operator info and stringified settings
 */
export interface DbActor {
  /** Operator/Actor ID */
  id?: number;

  /** Parent actor ID — present for sub-operators, absent for top-level operators */
  parentId?: number | null;

  /** Country code (e.g., "IL") */
  countryCode?: string;

  /** Party ID for OCPI integration */
  partyId?: string;

  /** Stringified JSON with all kiosk settings */
  settings?: string;
}

/**
 * Kiosk Settings (Parsed)
 * Internal model with parsed settings and metadata
 */
export interface KioskSettings {
  /** Operator ID */
  operatorId?: number;

  /** Parent actor ID — present for sub-operators, absent for top-level operators */
  parentId?: number | null;

  /** Country code */
  countryCode?: string;

  /** Party ID */
  partyId?: string;

  /** All parsed kiosk settings */
  settings: KioskSettingsJson;
}

/**
 * Update Kiosk Settings Request
 * Delta changes to send to API (only changed fields)
 */
export interface UpdateKioskSettingsRequest {
  [key: string]: any;
}
